import {
  createDocumentAssemblerTemplateCategory,
  DOCUMENT_ASSEMBLER_KEY_FORMAT_REGEX,
  DOCUMENT_ASSEMBLER_LIMITS,
  IDocumentAssemblerCreatedTemplateCategoryResponse,
  IDocumentAssemblerTemplateCategory,
} from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  Textarea,
} from '@commenda/kakashi';
import {
  messageFromApiErrorResponse,
  readJsonResponseAs,
  slugify,
} from '@commenda/utils';
import { useState } from 'react';

import { useNotification } from '../hooks/useNotification';

interface DocumentAssemblerCategoryCreateDialogProps {
  /** Seeded from whatever the author typed into the picker's search box. */
  initialName?: string;
  onClose: () => void;
  onCreated: (category: IDocumentAssemblerTemplateCategory) => void;
}

export const DocumentAssemblerCategoryCreateDialog = ({
  initialName = '',
  onClose,
  onCreated,
}: DocumentAssemblerCategoryCreateDialogProps) => {
  const { setNotification } = useNotification();

  const [name, setName] = useState(initialName);
  // trim().toLowerCase() is all the server does, so the slug has to be built here.
  const [key, setKey] = useState(slugify(initialName));
  const [isKeyEdited, setIsKeyEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onNameChange = (nextName: string) => {
    setName(nextName);
    // Stop rewriting a key the author has chosen for themselves.
    if (!isKeyEdited) setKey(slugify(nextName));
  };

  const trimmedName = name.trim();
  const isKeyValid = DOCUMENT_ASSEMBLER_KEY_FORMAT_REGEX.test(key);
  const canSave = Boolean(trimmedName) && isKeyValid && !isSaving;

  const onSubmit = async () => {
    setSubmitError(null);
    setIsSaving(true);

    // The request goes through the raw fetcher, which REJECTS on a network or CORS failure rather
    // than resolving with a bad status, so the catch is what keeps the button from spinning forever.
    try {
      const response = await createDocumentAssemblerTemplateCategory({
        key,
        name: trimmedName,
        description: description.trim() || undefined,
      });

      if (!response.ok) {
        setSubmitError(
          await messageFromApiErrorResponse(
            response,
            'The category could not be created.'
          )
        );
        return;
      }

      const body =
        await readJsonResponseAs<IDocumentAssemblerCreatedTemplateCategoryResponse>(
          response
        );

      if (!body?.templateCategory?.id) {
        setSubmitError('The category was created but could not be read back.');
        return;
      }

      setNotification({ show: true, valid: true, title: 'Category created' });
      onCreated({
        id: body.templateCategory.id,
        key,
        name: trimmedName,
        description: description.trim() || null,
        archivedAt: null,
        templateCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      onClose();
    } catch {
      setSubmitError(
        'The category could not be sent. Check your connection and try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader className="flex-col items-start gap-xs">
          <DialogTitle>Add a category</DialogTitle>
          <DialogDescription>
            The family of document a template belongs to. A workflow step can be
            scoped to one category, which is what stops an agreements step being
            offered a registration notice.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-xl p-3xl">
          {submitError ? (
            <Alert variant="destructive" description={submitError} />
          ) : null}

          <FieldWrapper>
            <Label htmlFor="document-assembler-category-name" required>
              Name
            </Label>
            <Input
              id="document-assembler-category-name"
              value={name}
              maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxNameLength}
              placeholder="Intercompany agreements"
              onChange={(event) => onNameChange(event.target.value)}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label htmlFor="document-assembler-category-key" required>
              Key
            </Label>
            <Input
              id="document-assembler-category-key"
              value={key}
              maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxKeyLength}
              placeholder="intercompany-agreements"
              error={
                key && !isKeyValid
                  ? 'Lower-case letters, numbers, and a single -, . or _ between them.'
                  : undefined
              }
              onChange={(event) => {
                setIsKeyEdited(true);
                setKey(event.target.value);
              }}
            />
            <p className="txt-extra-small-regular mt-xs text-text-secondary">
              A key never changes once it is set — a workflow step scoped to it
              would stop matching.
            </p>
          </FieldWrapper>

          <FieldWrapper>
            <Label htmlFor="document-assembler-category-description" optional>
              Description
            </Label>
            <Textarea
              id="document-assembler-category-description"
              rows={3}
              value={description}
              maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxDescriptionLength}
              onChange={(event) => setDescription(event.target.value)}
            />
          </FieldWrapper>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" size="sm" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            type="button"
            disabled={!canSave}
            isLoading={isSaving}
            onClick={onSubmit}
          >
            Add category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
